export function buildEnhancedSlides({ root, countermeasures, sectorParam, templateSequence }) {
  const data = root || {};
  const sectorsData = data || { sectors: [] };
  const sectors = Array.isArray(sectorsData.sectors) ? sectorsData.sectors : [];
  
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;

  const distributedPredictionTemplates = [
    'data-dashboard',
    'immersive',
    'comparison',
    'timeline',
    'factory-stamp',
    'propaganda-drop',
    'broadcast-interrupt',
    'wheat-paste',
    'print-press',
    'stencil-spray',
    'evidence-board',
    'evidence-scatter',
    'warning-expansion',
    'projector-sputter',
    'comparison-split',
    'grid',
    'glitch-art',
    'hologram',
    'matrix-rain',
    'explosive',
    'kaleidoscope',
    'vortex',
    'neon-city',
    'quantum',
    'prism',
    'liquid-metal',
    'cosmic',
    'shockwave'
  ];

  const blingExtraTemplates = ['shockwave', 'kaleidoscope'];

  const balancedPredictionRotation = (() => {
    const base = [];
    distributedPredictionTemplates.forEach((t) => base.push(t));
    distributedPredictionTemplates.forEach((t) => base.push(t));

    const extras = blingExtraTemplates.filter((t) => distributedPredictionTemplates.includes(t));
    if (extras.length === 0) return base;

    const rotation = base.slice();
    const baseLen = base.length;

    extras.forEach((t, i) => {
      const insertAt = Math.floor(((i + 1) * baseLen) / (extras.length + 1));
      rotation.splice(insertAt + i, 0, t);
    });

    return rotation;
  })();

  const sectorKeyById = {
    screen: 'SCREEN',
    games: 'GAMES',
    music: 'MUSIC',
    live: 'LIVE',
    creator: 'CREATOR',
    social: 'SOCIAL',
    education: 'EDUCATION',
    craft: 'CRAFT',
    fashion: 'FASHION',
    advertising: 'ADVERTISING',
    ai: 'AI',
    infrastructure: 'INFRASTRUCTURE'
  };

  const defaultTemplateSequence = [
    'title',
    'prism',
    'immersive',
    'liquid-metal',
    'timeline',
    'cosmic',
    'data-dashboard',
    'shockwave',
    'glitch-art',
    'grid',
    'hologram',
    'explosive',
    'data-dashboard',
    'matrix-rain',
    'prism',
    'evidence-board',
    'liquid-metal',
    'comparison',
    'vortex',
    'data-dashboard',
    'cosmic',
    'glitch-art',
    'shockwave',
    'hologram',
    'kaleidoscope',
    'evidence-scatter',
    'neon-city',
    'quantum',
    'evidence-board',
    'explosive',
    'grid',
    'vortex',
    'matrix-rain',
    'hologram',
    'kaleidoscope',
    'prism',
    'data-dashboard',
    'shockwave',
    'liquid-metal',
    'cosmic',
    'glitch-art',
    'comparison',
    'timeline',
    'immersive',
    'neon-city',
    'explosive',
    'quantum',
    'evidence-board',
    'grid',
    'vortex',
    'evidence-scatter',
    'matrix-rain',
    'kaleidoscope',
    'prism',
    'hologram',
    'data-dashboard',
    'neon-city',
    'vortex',
    'quantum',
    'shockwave',
    'cosmic'
  ];

  const resolvedTemplateSequence =
    Array.isArray(templateSequence) && templateSequence.length > 0 ? templateSequence : defaultTemplateSequence;

  const predictionTemplateSequence = resolvedTemplateSequence.filter((t) => t !== 'title');

  const getDistributedPredictionTemplate = (predictionIndex) =>
    balancedPredictionRotation[predictionIndex % balancedPredictionRotation.length] || 'immersive';

  const cmSlides = Array.isArray(countermeasures) ? countermeasures : [];
  const cmBySector = {};
  cmSlides.forEach((s) => {
    if (!s || !s.sector || s.sector === 'OBSOLETE.COM') return;
    if (!cmBySector[s.sector]) cmBySector[s.sector] = [];
    cmBySector[s.sector].push(s);
  });

  const sectorTitleImgById = {
    advertising: 'images/_0001_advertising-title.jpg',
    education: 'images/_0003_education-title.jpg',
    social: 'images/_0005_social-title.jpg',
    creator: 'images/_0007_creator-title.jpg',
    infrastructure: 'images/_0009_infrastructure-title.jpg',
    ai: 'images/_0011_ai-title.jpg',
    fashion: 'images/_0013_fashion-title.jpg',
    live: 'images/_0015_live-title.jpg',
    music: 'images/_0017_music-title.jpg',
    craft: 'images/_0019_craft-title.jpg',
    screen: 'images/_0021_screen-title.jpg',
    games: 'images/_0023_games-title.jpg'
  };

  const orderedIds = ['screen', 'games', 'music', 'fashion', 'creator', 'advertising', 'ai', 'live', 'craft', 'social', 'education', 'infrastructure'];

  let enhancedData = [];
  const fullEnhancedData = [];

  let predictionIndex = 0;

  if (sectorParam) {
    const sectorInfo = sectors.find((s) => s && s.id === sectorParam);
    if (sectorInfo) {
      const sectorKey = sectorKeyById[sectorParam] || String(sectorInfo.name || sectorParam).toUpperCase();
      const sectorDisplayName = sectorInfo.name || sectorKey;
      const sectorColIndex = orderedIds.indexOf(sectorParam);
      const sectorColNum = Math.max(0, sectorColIndex) + 1;
      const modelImg = `images/dmodel-${String(sectorColNum).padStart(2, '0')}.jpg`;
      const ctaImg = `images/dcta-${String(sectorColNum).padStart(2, '0')}.jpg`;
      const preds = Array.isArray(sectorInfo.predictions) ? sectorInfo.predictions.slice(0, 5) : [];
      const cmPreds = (cmBySector[sectorKey] || []).slice(0, 5);
      const titleImg = sectorTitleImgById[sectorParam] || 'images/title.jpg';
      const landingIntro = sectorInfo.landing && Array.isArray(sectorInfo.landing.intro) ? sectorInfo.landing.intro : [];
      const landingTagline = sectorInfo.landing && sectorInfo.landing.tagline ? sectorInfo.landing.tagline : '';
      const landingStory = [landingTagline, ...landingIntro].filter(Boolean).join('\n\n');

      enhancedData.push({
        sector: sectorKey,
        sectorId: sectorParam,
        sectorDisplayName,
        type: 'landing',
        title: sectorDisplayName,
        story: landingStory,
        logic: '',
        img: titleImg,
        template: 'sector-landing',
        impact: 0,
        impactType: 'none'
      });

      const predictions = cmPreds.length > 0 ? cmPreds : preds;
      while (predictions.length < 5) {
        predictions.push({ title: `${sectorDisplayName} — PLACEHOLDER`, story: '', logic: '', img: 'images/title.jpg' });
      }

      predictions.slice(0, 5).forEach((p, idx) => {
        const baseTemplate = getDistributedPredictionTemplate(predictionIndex++);
        const template = baseTemplate;
        
        enhancedData.push({
          sector: sectorKey,
          sectorId: sectorParam,
          sectorDisplayName,
          type: 'prediction',
          title: p && p.title ? p.title : `${sectorDisplayName} — PREDICTION ${idx + 1}`,
          story: p && p.story ? p.story : p && p.body ? p.body : '',
          mitigated_story: p && p.mitigated_story ? p.mitigated_story : null,
          countermeasures: p && p.countermeasures ? p.countermeasures : null,
          logic: p && p.logic ? p.logic : '',
          img: p && p.img ? p.img : p && p.image ? p.image : 'images/title.jpg',
          signal_validation: p && p.signal_validation ? p.signal_validation : null,
          signal_strength: p && p.signal_strength ? p.signal_strength : null,
          template,
          impact: Math.random() * 0.7 + 0.3,
          impactType: Math.random() > 0.5 ? 'crisis' : 'risk'
        });
      });

      const mp = sectorInfo.model_pitch || {};
      const mpStory = [mp.headline || '', ...(Array.isArray(mp.body) ? mp.body : [])].filter(Boolean).join('\n\n');
      enhancedData.push({
        sector: sectorKey,
        sectorId: sectorParam,
        sectorDisplayName,
        type: 'model',
        title: 'THE MODEL',
        story: mpStory,
        logic: '',
        img: modelImg,
        img_placeholder: `images/model_${sectorParam}.jpg`,
        template: 'sector-model',
        impact: 0,
        impactType: 'none'
      });

      enhancedData.push({
        sector: sectorKey,
        sectorId: sectorParam,
        sectorDisplayName,
        type: 'cta',
        title: sectorInfo.cta && sectorInfo.cta.title ? sectorInfo.cta.title : 'NEXT STEPS',
        story: '',
        logic: '',
        img: ctaImg,
        template: 'sector-cta',
        impact: 0,
        impactType: 'none',
        cta: sectorInfo.cta || {},
        contact: data && data.contact ? data.contact : 'hello@obsolete.com'
      });
    }
  }

  const globalSlide = cmSlides && cmSlides[0] ? cmSlides[0] : null;
  fullEnhancedData.push({
    sector: 'OBSOLETE.COM',
    sectorId: 'obsolete',
    sectorDisplayName: 'OBSOLETE.COM',
    type: 'global',
    title: globalSlide && globalSlide.title ? globalSlide.title : data && data.title ? data.title : '2029 CREATIVE INDUSTRIES',
    story: globalSlide && globalSlide.story ? globalSlide.story : data && data.description ? data.description : '',
    logic: globalSlide && globalSlide.logic ? globalSlide.logic : '',
    img: globalSlide && globalSlide.img ? globalSlide.img : 'images/title.jpg',
    template: 'title',
    impact: 0,
    impactType: 'none'
  });

  orderedIds.forEach((sid) => {
    const sectorInfo = sectors.find((s) => s && s.id === sid);
    if (!sectorInfo) return;
    const sectorKey = sectorKeyById[sid] || String(sectorInfo.name || sid).toUpperCase();
    const sectorDisplayName = sectorInfo.name || sectorKey;
    const sectorColIndex = orderedIds.indexOf(sid);
    const sectorColNum = Math.max(0, sectorColIndex) + 1;
    const modelImg = `images/dmodel-${String(sectorColNum).padStart(2, '0')}.jpg`;
    const ctaImg = `images/dcta-${String(sectorColNum).padStart(2, '0')}.jpg`;
    const preds = Array.isArray(sectorInfo.predictions) ? sectorInfo.predictions.slice(0, 5) : [];
    const cmPreds = (cmBySector[sectorKey] || []).slice(0, 5);
    const titleImg = sectorTitleImgById[sid] || 'images/title.jpg';
    const landingIntro = sectorInfo.landing && Array.isArray(sectorInfo.landing.intro) ? sectorInfo.landing.intro : [];
    const landingTagline = sectorInfo.landing && sectorInfo.landing.tagline ? sectorInfo.landing.tagline : '';
    const landingStory = [landingTagline, ...landingIntro].filter(Boolean).join('\n\n');

    fullEnhancedData.push({
      sector: sectorKey,
      sectorId: sid,
      sectorDisplayName,
      type: 'landing',
      title: sectorDisplayName,
      story: landingStory,
      logic: '',
      img: titleImg,
      template: 'sector-landing',
      impact: 0,
      impactType: 'none'
    });

    const predictions = cmPreds.length > 0 ? cmPreds : preds;
    while (predictions.length < 5) {
      predictions.push({ title: `${sectorDisplayName} — PLACEHOLDER`, story: '', logic: '', img: 'images/title.jpg' });
    }

    predictions.slice(0, 5).forEach((p, idx) => {
      const baseTemplate = getDistributedPredictionTemplate(predictionIndex++);
      const template = baseTemplate;
      
      fullEnhancedData.push({
        sector: sectorKey,
        sectorId: sid,
        sectorDisplayName,
        type: 'prediction',
        title: p && p.title ? p.title : `${sectorDisplayName} — PREDICTION ${idx + 1}`,
        story: p && p.story ? p.story : p && p.body ? p.body : '',
        mitigated_story: p && p.mitigated_story ? p.mitigated_story : null,
        countermeasures: p && p.countermeasures ? p.countermeasures : null,
        logic: p && p.logic ? p.logic : '',
        img: p && p.img ? p.img : p && p.image ? p.image : 'images/title.jpg',
        signal_validation: p && p.signal_validation ? p.signal_validation : null,
        signal_strength: p && p.signal_strength ? p.signal_strength : null,
        template,
        impact: Math.random() * 0.7 + 0.3,
        impactType: Math.random() > 0.5 ? 'crisis' : 'risk'
      });
    });

    const mp = sectorInfo.model_pitch || {};
    const mpStory = [mp.headline || '', ...(Array.isArray(mp.body) ? mp.body : [])].filter(Boolean).join('\n\n');
    fullEnhancedData.push({
      sector: sectorKey,
      sectorId: sid,
      sectorDisplayName,
      type: 'model',
      title: 'THE MODEL',
      story: mpStory,
      logic: '',
      img: modelImg,
      img_placeholder: `images/model_${sid}.jpg`,
      template: 'sector-model',
      impact: 0,
      impactType: 'none'
    });

    fullEnhancedData.push({
      sector: sectorKey,
      sectorId: sid,
      sectorDisplayName,
      type: 'cta',
      title: sectorInfo.cta && sectorInfo.cta.title ? sectorInfo.cta.title : 'NEXT STEPS',
      story: '',
      logic: '',
      img: ctaImg,
      template: 'sector-cta',
      impact: 0,
      impactType: 'none',
      cta: sectorInfo.cta || {},
      contact: data && data.contact ? data.contact : 'hello@obsolete.com'
    });
  });

  if (!sectorParam || enhancedData.length === 0) {
    enhancedData = fullEnhancedData;
  }

  return enhancedData;
}
